import React, { useEffect, useMemo, useRef, useState } from "react";
import type { CourseProgressSummary, PublicMetrics } from "../appTypes";
import PageState from "../components/PageState";
import type { ProductItem } from "../services/api";

interface OverviewCard {
  label: string;
  value: string;
  helper: string;
}

interface DashboardPageProps {
  overviewCards: OverviewCard[];
  courseProgressList: CourseProgressSummary[];
  products: ProductItem[];
  planilhaProduct: ProductItem | null;
  userName?: string;
  metrics: PublicMetrics;
  accessibleProductsCount: number;
  isLoggedIn: boolean;
  loadingData: boolean;
  pageError: string;
}

function normalizeBrandTitle(title: string) {
  return title.replace(/bulls ia/gi, "Blue Capital");
}

function buildProductHref(product: ProductItem) {
  if (product.isCourse) return `#/curso/${product.id}`;
  if (product.productUrl?.includes("/acesso/planilha")) return "#/ferramentas";
  return product.accessUrl || product.productUrl || product.checkoutUrl || "#/catalogo";
}

function buildProductCta(product: ProductItem) {
  if (product.hasAccess) return product.isCourse ? "Acessar agora" : "Abrir acesso";
  return "Liberar acesso";
}

function getAccessPanelCopy(product: ProductItem) {
  if (product.isCourse) {
    return product.hasAccess ? "Aulas e módulos liberados." : "Libere para assistir.";
  }

  if (/vip|comunidade/i.test(product.title)) {
    return product.hasAccess ? "Grupo e materiais disponíveis." : "Entrada pendente.";
  }

  if (/gerenciador|banca|planilha/i.test(product.title)) {
    return product.hasAccess ? "Ferramenta pronta para uso." : "Ative para usar.";
  }

  if (/bulls ia|blue capital/i.test(product.title)) {
    return product.hasAccess ? "Ambiente premium liberado." : "Libere o acesso premium.";
  }

  return product.hasAccess ? "Acesso disponível." : "Aguardando liberação.";
}

const DashboardPage: React.FC<DashboardPageProps> = ({
  overviewCards,
  courseProgressList,
  products,
  planilhaProduct,
  userName,
  metrics,
  accessibleProductsCount,
  isLoggedIn,
  loadingData,
  pageError,
}) => {
  const accessScrollRef = useRef<HTMLElement>(null);
  const courseScrollRef = useRef<HTMLDivElement>(null);
  const [accessScrollProgress, setAccessScrollProgress] = useState(0);
  const [activeAccessIndex, setActiveAccessIndex] = useState(0);
  const [courseScrollProgress, setCourseScrollProgress] = useState(0);
  const [activeCourseIndex, setActiveCourseIndex] = useState(0);

  const featuredAccessCards = useMemo(() => {
    const courseProduct =
      products.find((product) => product.isCourse) ?? null;
    const vipProduct =
      products.find((product) => /vip|comunidade/i.test(product.title)) ?? null;
    const blueCapitalProduct =
      products.find((product) => /bulls ia|blue capital/i.test(product.title)) ?? null;
    const bankrollProduct =
      products.find((product) => /gerenciador|banca|planilha/i.test(product.title)) ??
      planilhaProduct;

    const prioritized = [
      courseProduct,
      vipProduct,
      blueCapitalProduct,
      bankrollProduct,
    ].filter((product): product is ProductItem => Boolean(product));

    const seen = new Set(prioritized.map((product) => product.id));
    const fallback = products.filter((product) => !seen.has(product.id)).slice(0, 4);

    return [...prioritized, ...fallback].slice(0, 4).map((product) => ({
      ...product,
      displayTitle: product.isCourse
        ? "Aulas"
        : /vip|comunidade/i.test(product.title)
          ? "Grupo VIP"
          : /bulls ia|blue capital/i.test(product.title)
            ? "Blue Capital"
            : /gerenciador|banca|planilha/i.test(product.title)
              ? "Gerenciador de Banca"
              : normalizeBrandTitle(product.title),
    }));
  }, [planilhaProduct, products]);

  const blueCapitalStatus =
    featuredAccessCards.find((product) => product.displayTitle === "Blue Capital") ??
    featuredAccessCards[0];

  const accessibleCardsCount = featuredAccessCards.filter((product) => product.hasAccess).length;
  const lockedCardsCount = Math.max(featuredAccessCards.length - accessibleCardsCount, 0);
  const completedCoursesCount = courseProgressList.filter(
    (course) => course.progressPercent >= 100,
  ).length;
  const nextCourse =
    [...courseProgressList]
      .filter((course) => course.progressPercent < 100)
      .sort((current, next) => next.progressPercent - current.progressPercent)[0] ?? null;
  const mostAdvancedCourse =
    [...courseProgressList].sort(
      (current, next) => next.progressPercent - current.progressPercent,
    )[0] ?? null;
  const heroPills = [
    `${accessibleCardsCount} acessos prontos`,
    `${courseProgressList.length} trilhas monitoradas`,
    `${metrics.onlineUsers} pessoas online`,
  ];

  useEffect(() => {
    const container = accessScrollRef.current;

    if (!container || loadingData || !featuredAccessCards.length) {
      setAccessScrollProgress(0);
      setActiveAccessIndex(0);
      return;
    }

    const updateScrollState = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const maxScroll = Math.max(scrollWidth - clientWidth, 1);
      const progress = Math.min(Math.max(scrollLeft / maxScroll, 0), 1);
      const estimatedIndex = Math.round(progress * Math.max(featuredAccessCards.length - 1, 0));

      setAccessScrollProgress(progress);
      setActiveAccessIndex(estimatedIndex);
    };

    updateScrollState();
    container.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      container.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [featuredAccessCards.length, loadingData]);

  useEffect(() => {
    const container = courseScrollRef.current;

    if (!container || !courseProgressList.length) {
      setCourseScrollProgress(0);
      setActiveCourseIndex(0);
      return;
    }

    const updateScrollState = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const maxScroll = Math.max(scrollWidth - clientWidth, 1);
      const progress = Math.min(Math.max(scrollLeft / maxScroll, 0), 1);
      const estimatedIndex = Math.round(progress * Math.max(courseProgressList.length - 1, 0));

      setCourseScrollProgress(progress);
      setActiveCourseIndex(estimatedIndex);
    };

    updateScrollState();
    container.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      container.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [courseProgressList.length]);

  return (
    <>
      <section className="dashboard-premium-shell">
        <aside className="dashboard-side-rail glass-panel">
          <div className="dashboard-side-card">
            <span className="section-eyebrow">Área de membros</span>
            <h2 className="dashboard-side-title">
              Bem-vindo(a) de volta{userName ? `, ${userName.split(" ")[0]}` : ""}.
            </h2>
            <p className="section-description">Tudo em um só lugar.</p>
          </div>

          <div className="dashboard-side-stats">
            <article className="dashboard-side-stat">
              <span>Alunos ativos</span>
              <strong>{metrics.activeStudents}</strong>
              <p>Tempo real</p>
            </article>
            <article className="dashboard-side-stat">
              <span>Usuários online</span>
              <strong>{metrics.onlineUsers}</strong>
              <p>Agora</p>
            </article>
            {overviewCards.map((card) => (
              <article key={card.label} className="dashboard-side-stat">
                <span>{card.label}</span>
                <strong>{card.value}</strong>
                <p>{card.helper}</p>
              </article>
            ))}
            <article className="dashboard-side-stat">
              <span>Ferramentas liberadas</span>
              <strong>{accessibleProductsCount}</strong>
              <p>Prontas para abrir</p>
            </article>
          </div>

          <div className="dashboard-side-footer">
            <span className="dashboard-profile-pill">{userName || "Visitante"}</span>
            <div className="dashboard-side-actions">
              <a href="#/home" className="secondary-button">
                Home
              </a>
              <a href="#/ferramentas" className="secondary-button">
                Ferramentas
              </a>
            </div>
          </div>
        </aside>

        <div className="dashboard-main-stack">
          <section className="dashboard-premium-hero glass-panel">
            <div className="dashboard-premium-copy">
              <span className="dashboard-hero-kicker">Acesso premium</span>
              <h2 className="dashboard-premium-title">Área de membros</h2>
              <p className="section-description">
                {featuredAccessCards.length
                  ? featuredAccessCards.map((product) => product.displayTitle).join(", ")
                  : "Cursos e acessos centrais."}
              </p>

              <div className="dashboard-hero-pills">
                {heroPills.map((pill) => (
                  <span key={pill}>{pill}</span>
                ))}
              </div>

              <div className="dashboard-premium-actions">
                <a
                  href={
                    featuredAccessCards.find((product) => product.displayTitle === "Grupo VIP")
                      ? buildProductHref(
                          featuredAccessCards.find((product) => product.displayTitle === "Grupo VIP")!,
                        )
                      : "#/catalogo"
                  }
                  className="primary-button"
                >
                  Comunidade VIP
                </a>
                <a href="#/catalogo" className="secondary-button">
                  Abrir catálogo
                </a>
              </div>
            </div>

            <article className="dashboard-status-card">
              <span className="section-eyebrow">Status em tempo real</span>
              <div className="dashboard-status-inner">
                <small>{blueCapitalStatus ? blueCapitalStatus.displayTitle : "Blue Capital"}</small>
                <strong>{loadingData ? "Sincronizando" : "Online 24/7"}</strong>
                <p>Ambiente principal ativo.</p>
              </div>
            </article>
          </section>

          {!loadingData && !pageError ? (
            <section className="dashboard-insight-strip">
              <article className="dashboard-insight-card glass-panel dashboard-insight-card-primary">
                <span className="section-eyebrow">Próximo passo</span>
                <strong>
                  {nextCourse ? "Continuar estudo" : "Explorar novos acessos"}
                </strong>
                <p>
                  {nextCourse
                    ? `${nextCourse.progressPercent}% concluído em ${nextCourse.title}.`
                    : "Abra o catálogo."}
                </p>
                <a
                  href={nextCourse ? `#/curso/${nextCourse.courseId}` : "#/catalogo"}
                  className="secondary-button dashboard-insight-link"
                >
                  {nextCourse ? "Retomar curso" : "Abrir catálogo"}
                </a>
              </article>

              <article className="dashboard-insight-card glass-panel">
                <span className="section-eyebrow">Leitura rápida</span>
                <div className="dashboard-insight-metrics">
                  <div>
                    <strong>{completedCoursesCount}</strong>
                    <p>cursos concluídos</p>
                  </div>
                  <div>
                    <strong>{accessibleCardsCount}</strong>
                    <p>acessos liberados</p>
                  </div>
                  <div>
                    <strong>{lockedCardsCount}</strong>
                    <p>pendentes</p>
                  </div>
                </div>
              </article>

              <article className="dashboard-insight-card glass-panel">
                <span className="section-eyebrow">Em destaque</span>
                <strong>
                  {mostAdvancedCourse
                    ? mostAdvancedCourse.title
                    : blueCapitalStatus?.displayTitle || "Blue Capital"}
                </strong>
                <p>
                  {mostAdvancedCourse
                    ? `${mostAdvancedCourse.watchedLessons} aulas assistidas de ${mostAdvancedCourse.totalLessons}.`
                    : "Ambiente pronto."}
                </p>
              </article>
            </section>
          ) : null}

          {loadingData ? (
            <PageState
              eyebrow="Dashboard"
              title="Sincronizando acessos e indicadores"
              description="Consultando acessos e progresso."
              tone="loading"
            />
          ) : null}

          {!loadingData && pageError ? (
            <PageState
              eyebrow="Dashboard"
              title="Não foi possível montar o painel"
              description={pageError}
              tone="error"
              actionHref="#/home"
              actionLabel="Voltar para home"
            />
          ) : null}

          {!loadingData && featuredAccessCards.length ? (
            <section ref={accessScrollRef} className="dashboard-access-grid">
              {featuredAccessCards.map((product) => (
                <article key={product.id} className="dashboard-access-card glass-panel">
                  <div className="dashboard-access-banner">
                    {product.banner ? (
                      <img
                        src={product.banner}
                        alt={product.displayTitle}
                        className="dashboard-access-banner-image"
                      />
                    ) : null}
                    <div className="dashboard-access-banner-overlay" />
                    <strong>{product.displayTitle}</strong>
                  </div>

                  <div className="dashboard-access-body">
                    <h3>{product.displayTitle}</h3>
                    <p>{getAccessPanelCopy(product)}</p>

                    <span
                      className={`dashboard-access-badge ${
                        product.hasAccess ? "is-open" : "is-locked"
                      }`}
                    >
                      {product.hasAccess ? "Liberado" : "Pendente"}
                    </span>

                    <div className="dashboard-access-box">
                      <span>Acesso</span>
                      <strong>
                        {product.hasAccess ? "Incluído na sua conta" : "Liberação pendente"}
                      </strong>
                      <p>{product.hasAccess ? "Toque para abrir." : "Libere no catálogo."}</p>
                    </div>

                    <a href={buildProductHref(product)} className="primary-button dashboard-access-cta">
                      {buildProductCta(product)}
                    </a>
                  </div>
                </article>
              ))}
            </section>
          ) : null}

          {!loadingData && featuredAccessCards.length ? (
            <div className="home-courses-carousel-status dashboard-carousel-status" aria-hidden="true">
              <div className="home-courses-carousel-track">
                <span
                  className="home-courses-carousel-fill"
                  style={{ width: `${Math.max(accessScrollProgress * 100, featuredAccessCards.length > 1 ? 24 : 100)}%` }}
                />
              </div>
              <div className="home-courses-carousel-meta">
                <span>acessos disponíveis</span>
                <strong>
                  {String(Math.min(activeAccessIndex + 1, featuredAccessCards.length)).padStart(2, "0")}
                  {" / "}
                  {String(featuredAccessCards.length).padStart(2, "0")}
                </strong>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      <section className="dashboard-lower-grid">
        <section className="module-spotlight glass-panel dashboard-courses-panel">
          <div className="module-spotlight-copy dashboard-courses-heading">
            <span className="section-eyebrow">Progresso por curso</span>
            <h2 className="section-title">Cursos ativos</h2>
            <p className="section-description">Retome de onde parou.</p>

            {courseProgressList.length ? (
              <div className="dashboard-courses-badge">
                <strong>{courseProgressList.length}</strong>
                <span>cursos monitorados</span>
              </div>
            ) : null}
          </div>

          {!isLoggedIn ? (
            <p className="feedback-note">
              Entre para ver os cursos.
            </p>
          ) : null}

          {isLoggedIn && !loadingData && !courseProgressList.length ? (
            <p className="feedback-note">
              Nenhum curso liberado.
            </p>
          ) : null}

          {courseProgressList.length ? (
            <div ref={courseScrollRef} className="dashboard-course-grid">
              {courseProgressList.map((course, index) => (
                <article key={course.courseId} className="dashboard-course-card">
                  <div className="dashboard-course-rank">0{index + 1}</div>
                  <div className="dashboard-course-header">
                    <div>
                      <span className="dashboard-course-label">Curso liberado</span>
                      <h3>{course.title}</h3>
                    </div>
                    <strong>{course.progressPercent}%</strong>
                  </div>

                  <div className="dashboard-course-meter" aria-hidden="true">
                    <span style={{ width: `${course.progressPercent}%` }} />
                  </div>

                  <div className="dashboard-course-meta">
                    <span>{course.watchedLessons} aulas assistidas</span>
                    <span>{course.totalLessons} aulas no total</span>
                    <span>{course.totalModules} módulos</span>
                  </div>

                  <a href={`#/curso/${course.courseId}`} className="dashboard-course-cta">
                    Abrir curso
                  </a>
                </article>
              ))}
            </div>
          ) : null}

          {courseProgressList.length ? (
            <div className="home-courses-carousel-status dashboard-carousel-status" aria-hidden="true">
              <div className="home-courses-carousel-track">
                <span
                  className="home-courses-carousel-fill"
                  style={{ width: `${Math.max(courseScrollProgress * 100, courseProgressList.length > 1 ? 24 : 100)}%` }}
                />
              </div>
              <div className="home-courses-carousel-meta">
                <span>progresso por trilha</span>
                <strong>
                  {String(Math.min(activeCourseIndex + 1, courseProgressList.length)).padStart(2, "0")}
                  {" / "}
                  {String(courseProgressList.length).padStart(2, "0")}
                </strong>
              </div>
            </div>
          ) : null}
        </section>

        <aside className="dashboard-activity-panel glass-panel">
          <div className="dashboard-activity-header">
            <span className="section-eyebrow">Painel lateral</span>
            <h2 className="section-title">Sessão atual</h2>
            <p className="section-description">Conta e foco.</p>
          </div>

          <div className="dashboard-activity-list">
            <article className="dashboard-activity-card">
              <span>Conta</span>
              <strong>{userName || "Visitante"}</strong>
              <p>{isLoggedIn ? "Sessão ativa." : "Aguardando login."}</p>
            </article>

            <article className="dashboard-activity-card">
              <span>Ritmo atual</span>
              <strong>
                {mostAdvancedCourse
                  ? `${mostAdvancedCourse.progressPercent}% em ${mostAdvancedCourse.title}`
                  : "Sem trilha ativa"}
              </strong>
              <p>
                {mostAdvancedCourse
                  ? `${mostAdvancedCourse.watchedLessons} aulas concluídas.`
                  : "O progresso aparece aqui."}
              </p>
            </article>

            <article className="dashboard-activity-card">
              <span>Ambiente principal</span>
              <strong>{blueCapitalStatus?.displayTitle || "Blue Capital"}</strong>
              <p>
                {blueCapitalStatus?.hasAccess
                  ? "Acesso pronto."
                  : "Liberação pelo catálogo."}
              </p>
            </article>
          </div>

          <div className="dashboard-activity-actions">
            <a href="#/catalogo" className="secondary-button">
              Abrir catálogo
            </a>
            <a href="#/ferramentas" className="secondary-button">
              Abrir ferramentas
            </a>
          </div>
        </aside>
      </section>
    </>
  );
};

export default DashboardPage;
